/**
 * Settings > About & licences (W2-13 settings-keymap; DESIGN.md §4.20, D25).
 *
 * What Phiedit is, which build is running (public or with the local Phigros skin of a development setup), the
 * clean-room statement (no Phira or Re:PhiEdit code; GPL-3.0 Phira was never copied), the independence note, and
 * the licences of every library, font and document used, including Noto Sans SC.
 */

import type { AppServices } from '../../app/registry/types';
import { Icon } from '../../ui';
import type { SettingsText } from './hooks';
import { DOCUMENTS, FONTS, LIBRARIES, licenceUrl, type LicenceEntry } from './licences';
import { m } from './settings.messages';
import s from './Settings.module.css';

export interface AboutSectionProps {
  readonly services: AppServices;
  readonly text: SettingsText;
}

function LicenceTable(props: {
  readonly entries: readonly LicenceEntry[];
  readonly text: SettingsText;
  readonly caption: string;
}) {
  const { t } = props.text;
  return (
    <table className={s.simpleTable}>
      <caption className={s.tableCaption}>{props.caption}</caption>
      <thead>
        <tr>
          <th scope="col">{t(m.licColName)}</th>
          <th scope="col">{t(m.licColUse)}</th>
          <th scope="col">{t(m.licColLicence)}</th>
        </tr>
      </thead>
      <tbody>
        {props.entries.map((e) => (
          <tr key={e.name}>
            <td>
              <a href={e.url} target="_blank" rel="noreferrer noopener" className={s.link}>
                {e.name}
                <Icon name="ExternalLink" size={12} label={{ key: m.opensInNewTab }} />
              </a>
            </td>
            <td>{t(e.use)}</td>
            <td>
              <a href={licenceUrl(e.licence)} target="_blank" rel="noreferrer noopener" className={s.link}>
                {e.licence}
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AboutSection(props: AboutSectionProps) {
  const { text } = props;
  const { t } = text;
  const publicBuild = import.meta.env.VITE_PUBLIC_BUILD === 'true';
  const localSkin = import.meta.env.VITE_HAS_LOCAL_SKIN === 'true';
  return (
    <div className={s.about}>
      <div className={s.aboutHero}>
        <Icon name="Music4" size={28} />
        <div>
          <h3 className={s.blockTitle}>{t(m.aboutName)}</h3>
          <p className={s.blockDesc}>{t(m.aboutTagline)}</p>
        </div>
      </div>
      <ul className={s.facts}>
        <li data-level="info">
          <Icon name="Package" size={16} />
          <span>{t(publicBuild ? m.aboutBuildPublic : m.aboutBuildLocal)}</span>
        </li>
        {localSkin && (
          <li data-level="info">
            <Icon name="FolderOpen" size={16} />
            <span>{t(m.aboutLocalSkin)}</span>
          </li>
        )}
        <li data-level="ok">
          <Icon name="ShieldCheck" size={16} />
          <span>{t(m.aboutCleanRoom)}</span>
        </li>
        <li data-level="info">
          <Icon name="Info" size={16} />
          <span>{t(m.aboutIndependent)}</span>
        </li>
      </ul>
      <LicenceTable entries={LIBRARIES} text={text} caption={t(m.licLibraries)} />
      <LicenceTable entries={FONTS} text={text} caption={t(m.licFonts)} />
      <LicenceTable entries={DOCUMENTS} text={text} caption={t(m.licDocuments)} />
      <p className={s.blockDesc}>{t(m.licFontsNote)}</p>
    </div>
  );
}
